import { LANDING_PAGE_REDIRECT } from "@/abilities/shop/config/constants";
import { useShopStatusStore } from "@/abilities/shop/features/shop-core/store/shop-status.store";
import { SalesForce } from "@/abilities/shop/types/salesforce-shop";
import { publicClient } from "@/shared/services/apollo-clients/public-client";
import { decodeJwtPayload } from "@/shared/utils/jwt/token-parser";
import { safeJoinURL } from "@/shared/utils/route-utils";
import { get, set } from "@vueuse/core";
import { gql } from "graphql-tag";
import { ref } from "vue";

// Create a singleton token store that persists across component lifecycles
const jwtToken = ref<string | undefined>(undefined);

export function useSalesforceAuth() {
    const setJwtToken = (token: string) => {
        set(jwtToken, token);
    };

    const getJwtToken = (): string | undefined => {
        return get(jwtToken);
    };

    const getTokenDetails = (): SalesForce.Token | undefined => {
        if (jwtToken.value) {
            const decoded = decodeJwtPayload(jwtToken.value);
            if (!decoded) return undefined;
            return {
                accessToken: (decoded.accessToken as string | undefined) ?? "",
                userId: (decoded.userId as string | undefined) ?? "",
                refreshToken: (decoded.refreshToken as string | undefined) ?? "",
            };
        } else {
            return undefined;
        }
    };

    const getUserId = (): string | undefined => {
        return getTokenDetails()?.userId;
    };

    const getAccessToken = (): string | undefined => {
        return getTokenDetails()?.accessToken;
    };

    const getTenantId = async (): Promise<string | undefined> => {
        try {
            const { data } = await publicClient.query({
                query: gql`
                    query tenantId {
                        tenant {
                            id
                        }
                    }
                `,
            });
            return data.tenant.id;
        } catch (error) {
            console.error("Failed to get tenant ID:", error);
            return undefined;
        }
    };

    const connect = async (): Promise<void> => {
        const tenantId = await getTenantId();
        if (!tenantId) {
            return;
        }

        // Save the current route as the entry path for later redirect
        const store = useShopStatusStore();
        const callbackCurrentRouteAsString = window.location.pathname + window.location.search;
        store.setAppEntryPath(callbackCurrentRouteAsString);

        const { shopServiceUrl } = store.salesforceConfig() ?? {};

        if (!shopServiceUrl) {
            console.error("No shop service url found");
            return;
        }

        // Redirect to shop service url with salesforce login endpoint for tenant
        const redirect = safeJoinURL(shopServiceUrl, `salesforce/login/for/${tenantId}`);

        // Add callback url to redirect back to the frontend to shop landing page
        const callbackURL = safeJoinURL(window.location.origin, LANDING_PAGE_REDIRECT);
        redirect.searchParams.append("callbackURL", callbackURL.href);

        // Redirect to shop service
        window.location.href = redirect.href;
    };

    const refreshToken = async (): Promise<void> => {
        const tenantId = await getTenantId();
        if (!tenantId) {
            return;
        }

        const store = useShopStatusStore();
        const { shopServiceUrl } = store.salesforceConfig() ?? {};

        if (!shopServiceUrl) {
            console.error("No shop service url found");
            return;
        }

        try {
            const tokenUrl = safeJoinURL(shopServiceUrl, `salesforce/refresh/for/${tenantId}`);
            const tokenResponse = await fetch(tokenUrl);
            const response = await tokenResponse.json();
            set(jwtToken, response.salesforceShopToken);
        } catch (error) {
            console.error("Failed to refresh Salesforce token:", error);
            throw error;
        }
    };

    return {
        connect,
        getAccessToken,
        getJwtToken,
        getUserId,
        setJwtToken,
        refreshToken,
        getTenantId,
    };
}
